import React, { useRef, useEffect, useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  Bell, Smartphone, MapPin, X, ExternalLink, Clock, BookmarkCheck,
  ChevronsLeft, ChevronsRight, ChevronDown, ChevronRight, Power, Moon, Sun, SunDim,
} from 'lucide-react'
import { useSavedViewsStore } from '../../store/savedViewsStore'
import { viewToPath } from '../ui/SavedViewMenu'
import useAuthStore from '../../store/authStore.js'
import { useSettingsStore } from '../../store/settingsStore.js'
import { usePolicyStore } from '../../store/usePolicyStore.js'
import { useSiteFilterStore } from '../../store/siteFilterStore.js'
import { useLanguage } from '../../i18n/index.jsx'
import { cls } from '../../utils/helpers.js'
import { useUnreadCount, useSites } from '../../hooks/useData.js'
import { useChannels } from '../../hooks/useChannels.js'
import { isNavKeyAvailable, getNavKeyStatus } from '../../config/featureModules.js'
import DemoTimer from './DemoTimer.jsx'
import useRecentProductions from '../../hooks/useRecentProductions.js'
import useCollapsedSections from '../../hooks/useCollapsedSections.js'
import SidebarNavGroup from './SidebarNavGroup.jsx'
import { makeGroupIsActive } from './navActiveGroup.js'
import SidebarSearch from './SidebarSearch.jsx'
import type { NavItem, NavGroup, NavChild } from './navConstants.jsx'
import {
  MAIN_ITEMS, INVENTORY_GROUP, PRODUCTIONS_GROUP,
  NETWORK_GROUP, LIBRARY_GROUP,
  REPORTS_ITEM, PEOPLE_ITEM, ADMIN_GROUP,
  PLATFORM_NAV, CREW_GROUP, TOURING_GROUP,
} from './navConstants.jsx'
import type { User } from '../../types/domain'

interface RailFlyoutButtonProps {
  icon: React.ReactNode
  tip: string
  label: string
  children: React.ReactNode
}

// Collapsed-rail flyout — gives Pinned Views / Recent Productions a reachable
// home in icon-only mode instead of dropping them entirely. Self-manages its
// open state; closes on outside-click, Escape, or any link click inside.
function RailFlyoutButton({ icon, tip, label, children }: RailFlyoutButtonProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 62 })
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    document.addEventListener('keydown', onEsc)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!open) {
      const rect = e.currentTarget.getBoundingClientRect()
      const aside = e.currentTarget.closest('aside')
      const margin = 8
      const estHeight = Math.min((React.Children.count(children) + 1) * 36 + 8, window.innerHeight - margin * 2)
      const maxTop = window.innerHeight - estHeight - margin
      setPos({
        top: Math.max(margin, Math.min(rect.top, maxTop)),
        left: (aside ? aside.getBoundingClientRect().right : 70) + 4,
      })
    }
    setOpen(o => !o)
  }

  return (
    <div style={{ position: 'relative' }} ref={wrapRef}>
      <button
        type="button"
        className="nav-item"
        onClick={toggle}
        data-sb-tip={tip}
        aria-label={tip}
        aria-expanded={open ? 'true' : 'false'}
        style={{ width: '100%', border: 'none', background: 'none', cursor: 'pointer', justifyContent: 'center' }}
      >
        <span className="nav-icon">{icon}</span>
      </button>
      {open && (
        <div
          className="sidebar-flyout"
          role="presentation"
          style={{ position: 'fixed', left: pos.left, top: pos.top }}
          onClick={() => setOpen(false)}
        >
          <div className="sidebar-flyout-title">{label}</div>
          {children}
        </div>
      )}
    </div>
  )
}

interface LanguageOption {
  code: string
  label: string
  short: string
}

interface SearchResult {
  icon?: React.ReactNode
  name: string
  type: string
  [key: string]: unknown
}

interface AppSidebarProps {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  logoUrl?: string | null
  appName?: string | null
  searchQuery: string
  onSearch: (value: string) => void
  searchResults: SearchResult[]
  showSearchDropdown: boolean
  setShowSearchDropdown: (show: boolean) => void
  searchHighlight: number
  setSearchHighlight: (index: number) => void
  onSearchSelect: (result: SearchResult) => void
  onSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  theme: string
  toggleTheme: () => void
  getThemeTitle: () => string
  onShowProfile: () => void
  user: User | null | undefined
  lang: string
  setLang: (lang: string) => void
  languageOptions: LanguageOption[]
  platformMode: boolean
  onOpenCommandPalette: () => void
}

export default function AppSidebar({
  sidebarCollapsed, toggleSidebar,
  logoUrl, appName,
  searchQuery, onSearch, searchResults, showSearchDropdown, setShowSearchDropdown,
  searchHighlight, setSearchHighlight, onSearchSelect, onSearchKeyDown,
  theme, toggleTheme, getThemeTitle,
  onShowProfile, user,
  lang, setLang, languageOptions,
  platformMode,
  onOpenCommandPalette,
}: AppSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAdmin, isGlobalAdmin, logout } = useAuthStore()
  const { siteIds, toggleSite, clearFilter, recents } = useSiteFilterStore()
  const { t } = useLanguage()
  const { data: sites = [] } = useSites()
  const { data: unreadData } = useUnreadCount()
  const unreadCount = unreadData?.count || 0
  const { channels: commsChannels } = useChannels()
  const hasCommsUnread = commsChannels.some(c => c.has_unread)
  const { recents: recentProductions } = useRecentProductions()
  // Brighella v2 (ADR-013 P8) — pinned saved views render as sidebar links.
  const pinnedViews = useSavedViewsStore((s) => s.pinned)
  const loadPinnedViews = useSavedViewsStore((s) => s.loadPinned)
  const [recentOpen, toggleRecentOpen] = useCollapsedSections('recentProductions', true)
  const [pinnedOpen, togglePinnedOpen] = useCollapsedSections('pinnedViews', true)
  const [siteDropOpen, setSiteDropOpen] = React.useState(false)
  const [flyoutLeft, setFlyoutLeft] = useState(0)
  const [flyoutGroup, setFlyoutGroup] = useState<string | null>(null)
  const [flyoutTop, setFlyoutTop] = useState(0)
  const [avatarBroken, setAvatarBroken] = useState(false)
  const flyoutRef = useRef<HTMLDivElement>(null)
  // Collapsed-rail tooltip — one shared element, positioned from the
  // hovered/focused item's rect (events delegated off the <aside>).
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null)

  const showTip = (e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    if (!sidebarCollapsed || flyoutGroup) return
    const el = (e.target as HTMLElement).closest?.('[data-sb-tip]')
    if (!el) return
    const r = el.getBoundingClientRect()
    setTip({ label: el.getAttribute('data-sb-tip') ?? '', top: r.top + r.height / 2 })
  }
  const hideTip = (e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    const el = (e.target as HTMLElement).closest?.('[data-sb-tip]')
    if (!el || !el.contains((e as React.FocusEvent).relatedTarget as Node)) setTip(null)
  }
  useEffect(() => { if (!sidebarCollapsed || flyoutGroup) setTip(null) }, [sidebarCollapsed, flyoutGroup])

  useEffect(() => { setAvatarBroken(false) }, [user?.avatar_url])

  // Load the caller's pinned saved views once per mount (tenant mode only).
  useEffect(() => {
    if (!platformMode) loadPinnedViews()
  }, [platformMode, loadPinnedViews])

  useEffect(() => {
    if (!siteDropOpen) return
    const close = () => setSiteDropOpen(false)
    const timer = setTimeout(() => document.addEventListener('click', close), 0)
    return () => { clearTimeout(timer); document.removeEventListener('click', close) }
  }, [siteDropOpen])

  useEffect(() => {
    if (!flyoutGroup) return
    const close = (e: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target as Node)) setFlyoutGroup(null)
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', close), 0)
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', close) }
  }, [flyoutGroup])

  useEffect(() => { setFlyoutGroup(null) }, [location.pathname])

  const adminRole = isAdmin()
  const { enabledModules, disabledItems, planFeatures, gbEnabled, platformModules, platformDisabledModules } = useSettingsStore()
  const policyModules = usePolicyStore(s => s.modules)
  const filterCtx = { adminRole, enabledModules, disabledItems, planFeatures, settingsFlags: { gbEnabled }, platformModules, platformDisabledModules, policyModules } as Record<string, unknown>

  const filterItems = (items: NavItem[]) => items.filter(item => isNavKeyAvailable(item.key, filterCtx))
  // Wave 3c — keep module/plan-gated items in the rendered list with a lock
  // affordance, instead of silently dropping them. Role/settings/item/platform
  // gates still hide entirely (no upgrade path).
  const filterGroup = (group: NavGroup): NavGroup | null => {
    const children: NavChild[] = []
    for (const c of group.children) {
      if (!('key' in c)) { children.push(c); continue } // subHeader markers pass through
      const status = getNavKeyStatus(c.key, filterCtx)
      if (status.available) {
        children.push(c)
      } else if (status.reason === 'module' || status.reason === 'plan') {
        children.push({ ...c, locked: true, lockReason: status.reason, moduleKey: status.moduleKey })
      }
      // role/settings/item/platform → hidden
    }
    // Drop trailing/leading orphan subHeaders + adjacent dupes after filter.
    const hasVisibleChild = children.some(c => 'key' in c)
    return hasVisibleChild ? { ...group, children } : null
  }

  const filteredMain        = filterItems(MAIN_ITEMS)
  const filteredInventory   = filterGroup(INVENTORY_GROUP)
  const filteredProductions = filterGroup(PRODUCTIONS_GROUP)
  const filteredTouring     = filterGroup(TOURING_GROUP)
  const filteredCrew        = filterGroup(CREW_GROUP)
  const filteredNetwork     = filterGroup(NETWORK_GROUP)
  const filteredLibrary     = filterGroup(LIBRARY_GROUP)
  // Wave 3b — Reports + People are top-level hub doors (not groups). They
  // still get gated by isNavKeyAvailable through the items[] code path.
  const filteredReports     = filterItems([REPORTS_ITEM])[0] || null
  const filteredPeopleItem  = filterItems([PEOPLE_ITEM])[0] || null
  const filteredAdmin       = filterGroup(ADMIN_GROUP)

  const groupIsActive = makeGroupIsActive(location.pathname, [
    filteredInventory,
    filteredProductions,
    filteredTouring,
    filteredCrew,
    filteredNetwork,
    filteredLibrary,
    filteredAdmin,
  ])

  const renderNavGroup = (group: NavGroup | null) => group && (
    <SidebarNavGroup
      key={group.key}
      group={group}
      sidebarCollapsed={sidebarCollapsed}
      groupIsActive={groupIsActive}
      flyoutGroup={flyoutGroup}
      setFlyoutGroup={setFlyoutGroup}
      flyoutTop={flyoutTop}
      setFlyoutTop={setFlyoutTop}
      flyoutRef={flyoutRef}
      flyoutLeft={flyoutLeft}
      setFlyoutLeft={setFlyoutLeft}
    />
  )

  // ── Platform Mode ──────────────────────────────────────────────────────────
  if (platformMode) {
    return (
      <aside className={cls('sidebar', sidebarCollapsed && 'sidebar-collapsed')}
        onMouseOver={showTip} onMouseOut={hideTip} onFocus={showTip} onBlur={hideTip}>
        <div className="sidebar-brand">
          <button type="button" onClick={() => navigate('/super-admin')}
            aria-label={t('platformAdminHome')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textDecoration: 'none', color: 'inherit', minWidth: 0, background: 'transparent', border: 'none', padding: 0, font: 'inherit', textAlign: 'left' }}>
            <img src="/logo.png" alt="Brighella" className="brand-icon" style={{ width: 28, height: 28, objectFit: 'contain' }} loading="eager" width={28} height={28} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }} className="brand-name-wrap">
              <span className="brand-name">Platform Admin</span>
              <span className="brand-company" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 500 }}>Brighella</span>
            </div>
          </button>
        </div>
        <nav className="sidebar-nav">
          <p className="nav-section-label">{t('navSectionPlatform')}</p>
          {PLATFORM_NAV.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              data-sb-tip={sidebarCollapsed ? (t(item.key) || item.key) : undefined}
              aria-label={sidebarCollapsed ? (t(item.key) || item.key) : undefined}
              className={({ isActive }) => cls('nav-item', isActive && 'nav-active')}>
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{t(item.key) || item.key}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="user-chip" onClick={onShowProfile} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowProfile() } }}
            style={{ cursor: 'pointer', width: '100%' }}>
            <div className="user-avatar">{user?.username?.[0]?.toUpperCase() || 'U'}</div>
            <div className="user-info"><span className="user-name">{user?.username}</span></div>
          </div>
          <div className="sidebar-footer-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')} title={t('navBackToTenant')} aria-label={t('navBackToTenant')}>
              <ExternalLink size={16} />
            </button>
            <button className="btn btn-ghost btn-sm" onClick={logout} title={t('logout')} aria-label={t('logout')}><Power size={16} /></button>
          </div>
        </div>
        {sidebarCollapsed && tip && (
          <div className="sidebar-tooltip" style={{ left: 76, top: tip.top }}>{tip.label}</div>
        )}
      </aside>
    )
  }

  // ── Tenant Mode ────────────────────────────────────────────────────────────
  return (
    <aside className={cls('sidebar', sidebarCollapsed && 'sidebar-collapsed')}
      onMouseOver={showTip} onMouseOut={hideTip} onFocus={showTip} onBlur={hideTip}>
      <div className="sidebar-brand">
        <button type="button" onClick={() => navigate('/')}
          aria-label={`${appName || 'Brighella'} home`}
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textDecoration: 'none', color: 'inherit', minWidth: 0, background: 'transparent', border: 'none', padding: 0, font: 'inherit', textAlign: 'left' }}>
          {logoUrl ? (
            <img src={logoUrl ?? undefined} alt="Logo" style={{ height: 32, width: 'auto', maxWidth: 120, objectFit: 'contain' }} onError={e => { const t = e.currentTarget; t.onerror = null; t.src = '/logo.png' }} loading="eager" />
          ) : (
            <img src="/logo.png" alt="Brighella" className="brand-icon" style={{ width: 28, height: 28, objectFit: 'contain' }} loading="eager" width={28} height={28} />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }} className="brand-name-wrap">
            <span className="brand-name">{appName || 'Brighella'}</span>
            <span className="brand-company" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 500, letterSpacing: '0.02em', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
              Powered by Brighella
            </span>
          </div>
        </button>
        <button
          className="sidebar-collapse-btn"
          onClick={toggleSidebar}
          title={sidebarCollapsed ? t('expandSidebar') : t('collapseSidebar')}
          aria-label={t('ariaToggleSidebar')}
          style={{ display: 'flex', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, lineHeight: 1, flexShrink: 0, marginLeft: sidebarCollapsed ? 0 : 'auto' }}
        >
          {sidebarCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
        </button>
      </div>

      <SidebarSearch
        value={searchQuery}
        onChange={onSearch}
        results={searchResults}
        showDropdown={showSearchDropdown}
        setShowDropdown={setShowSearchDropdown}
        highlight={searchHighlight}
        setHighlight={setSearchHighlight}
        onSelect={onSearchSelect}
        onKeyDown={onSearchKeyDown}
        t={t}
        collapsed={sidebarCollapsed}
        onCollapsedClick={onOpenCommandPalette}
      />

      {sites.length > 1 && (sidebarCollapsed ? (
        <div className="site-filter-wrap" style={{ padding: '0 0.25rem', marginBottom: '0.5rem' }}>
          <button
            type="button"
            className="nav-item"
            onClick={toggleSidebar}
            data-sb-tip={t('allSites')}
            aria-label={t('allSites')}
            style={{ width: '100%', border: 'none', background: 'none', cursor: 'pointer', justifyContent: 'center' }}
          >
            <span className="nav-icon" style={{ position: 'relative' }}>
              <MapPin size={16} style={{ color: siteIds.length ? 'var(--accent)' : undefined }} />
              {siteIds.length > 0 && (
                <span style={{ position: 'absolute', top: -2, right: -4, width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
              )}
            </span>
          </button>
        </div>
      ) : (
        <div className="site-filter-wrap" style={{ padding: '0 0.75rem', marginBottom: '0.5rem' }}>
          <div
            role="button"
            tabIndex={0}
            style={{ width: '100%', padding: '6px 10px', fontSize: 'var(--text-sm)', background: 'var(--bg-elevated)', border: `1px solid ${siteIds.length ? 'var(--accent)' : 'var(--border-default)'}`, borderRadius: 'var(--radius)', color: 'var(--text-primary)', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}
            onClick={() => setSiteDropOpen(o => !o)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSiteDropOpen(o => !o) } }}
          >
            <MapPin size={12} style={{ flexShrink: 0, color: siteIds.length ? 'var(--accent)' : 'var(--text-muted)' }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {siteIds.length ? sites.filter(s => siteIds.includes(String(s.id))).map(s => s.name).join(', ') || `${siteIds.length} sites` : t('allSites')}
            </span>
            {siteIds.length > 0 && (
              <button onClick={e => { e.stopPropagation(); clearFilter() }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted)', display: 'flex' }} aria-label={t('clearFilter')}>
                <X size={12} />
              </button>
            )}
          </div>
          {siteDropOpen && (
            <div style={{ position: 'absolute', left: 12, right: 12, zIndex: 'var(--z-overlay)', marginTop: 4, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
              {recents.length > 0 && (() => {
                const recentSites = recents.map(id => sites.find(s => String(s.id) === id)).filter((s): s is typeof sites[number] => s != null)
                const recentIds = new Set(recentSites.map(s => String(s.id)))
                const restSites = sites.filter(s => !recentIds.has(String(s.id)))
                return (
                  <>
                    <div style={{ padding: '4px 10px 2px', fontSize: 'var(--text-tiny)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('siteFilterRecent')}
                    </div>
                    {recentSites.map(s => (
                      <label key={`recent-${s.id}`} className="site-filter-label">
                        <input type="checkbox" checked={siteIds.includes(String(s.id))} onChange={() => toggleSite(s.id)} style={{ accentColor: 'var(--accent)' }} />
                        <Clock size={11} style={{ color: 'var(--text-muted)' }} />
                        {s.name}
                      </label>
                    ))}
                    {restSites.length > 0 && (
                      <div style={{ height: 1, background: 'var(--border-default)', margin: '2px 0' }} />
                    )}
                    {restSites.map(s => (
                      <label key={s.id} className="site-filter-label">
                        <input type="checkbox" checked={siteIds.includes(String(s.id))} onChange={() => toggleSite(s.id)} style={{ accentColor: 'var(--accent)' }} />
                        <MapPin size={11} style={{ color: 'var(--text-muted)' }} />
                        {s.name}
                      </label>
                    ))}
                  </>
                )
              })()}
              {recents.length === 0 && sites.map(s => (
                <label key={s.id} className="site-filter-label">
                  <input type="checkbox" checked={siteIds.includes(String(s.id))} onChange={() => toggleSite(s.id)} style={{ accentColor: 'var(--accent)' }} />
                  <MapPin size={11} style={{ color: 'var(--text-muted)' }} />
                  {s.name}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}

      <nav className="sidebar-nav">
        <p className="nav-section-label">{t('navSectionMain')}</p>
        {filteredMain.map((item: NavItem) => (
          <NavLink key={item.to} to={item.to} end
            data-sb-tip={sidebarCollapsed ? (t(item.key) || item.key) : undefined}
            aria-label={sidebarCollapsed ? (t(item.key) || item.key) : undefined}
            className={({ isActive }) => cls('nav-item', isActive && 'nav-active')}>
            <span className="nav-icon" style={{ position: 'relative' }}>
              {item.icon}
              {item.key === 'navComms' && hasCommsUnread && (
                <span style={{ position: 'absolute', top: -2, right: -4, width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
              )}
            </span>
            <span className="nav-label">{t(item.key) || item.key}</span>
          </NavLink>
        ))}

        {renderNavGroup(filteredCrew)}

        <div className="nav-divider" style={{ height: 1, background: 'var(--border-subtle)', margin: '2px 16px' }} />
        {renderNavGroup(filteredTouring)}
        {renderNavGroup(filteredInventory)}
        {renderNavGroup(filteredProductions)}
        {renderNavGroup(filteredNetwork)}
        {renderNavGroup(filteredLibrary)}

        {adminRole && (
          <>
            <div className="nav-divider" style={{ height: 1, background: 'var(--border-subtle)', margin: '2px 16px' }} />
            <p className="nav-section-label" style={{ marginTop: '0.75rem' }}>{t('navSectionAdmin')}</p>
            {/* Wave 3b — Reports + People hub doors as direct NavLinks. */}
            {filteredReports && (
              <NavLink
                key={filteredReports.to}
                to={filteredReports.to}
                data-sb-tip={sidebarCollapsed ? (t(filteredReports.key) || filteredReports.key) : undefined}
                aria-label={sidebarCollapsed ? (t(filteredReports.key) || filteredReports.key) : undefined}
                className={({ isActive }) => cls('nav-item', isActive && 'nav-active')}
              >
                <span className="nav-icon">{filteredReports.icon}</span>
                <span className="nav-label">{t(filteredReports.key) || filteredReports.key}</span>
              </NavLink>
            )}
            {filteredPeopleItem && (
              <NavLink
                key={filteredPeopleItem.to}
                to={filteredPeopleItem.to}
                data-sb-tip={sidebarCollapsed ? (t(filteredPeopleItem.key) || filteredPeopleItem.key) : undefined}
                aria-label={sidebarCollapsed ? (t(filteredPeopleItem.key) || filteredPeopleItem.key) : undefined}
                className={({ isActive }) => cls('nav-item', isActive && 'nav-active')}
              >
                <span className="nav-icon">{filteredPeopleItem.icon}</span>
                <span className="nav-label">{t(filteredPeopleItem.key) || filteredPeopleItem.key}</span>
              </NavLink>
            )}
            {renderNavGroup(filteredAdmin)}
          </>
        )}
      </nav>

      {pinnedViews.length > 0 && (sidebarCollapsed ? (
        <div style={{ paddingTop: '0.5rem', borderTop: '1px solid var(--border-subtle)', marginTop: '0.5rem' }}>
          <RailFlyoutButton
            icon={<BookmarkCheck size={16} />}
            tip={t('savedViewsPinnedSection')}
            label={t('savedViewsPinnedSection')}
          >
            {pinnedViews.map(v => (
              <NavLink key={v.id} to={viewToPath(v)} className="nav-item">
                <BookmarkCheck size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} aria-hidden="true" />
                <span className="nav-label" style={{ display: 'inline' }}>{v.name}</span>
              </NavLink>
            ))}
          </RailFlyoutButton>
        </div>
      ) : (
        <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border-subtle)', marginTop: '0.75rem' }}>
          <button
            type="button"
            onClick={togglePinnedOpen}
            aria-expanded={pinnedOpen}
            aria-controls="sidebar-section-pinned"
            title={pinnedOpen ? t('collapseSection') : t('expandSection')}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, width: '100%',
              border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
              fontSize: 'var(--text-tiny)', fontWeight: 700, color: 'var(--text-muted)',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '0 12px 4px',
            }}
          >
            {pinnedOpen ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
            <span>{t('savedViewsPinnedSection')}</span>
          </button>
          {pinnedOpen && (
            <div id="sidebar-section-pinned">
              {pinnedViews.map(v => (
                <NavLink
                  key={v.id}
                  to={viewToPath(v)}
                  className={({ isActive }) => isActive ? 'nav-item nav-active' : 'nav-item'}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', fontSize: 'var(--text-caption)' }}
                >
                  <BookmarkCheck size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} aria-hidden="true" />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ))}

      {recentProductions.length > 0 && (sidebarCollapsed ? (
        <div style={{ paddingTop: '0.5rem', borderTop: '1px solid var(--border-subtle)', marginTop: '0.5rem' }}>
          <RailFlyoutButton
            icon={<Clock size={16} />}
            tip={t('recentProductions')}
            label={t('recentProductions')}
          >
            {recentProductions.map(p => (
              <NavLink key={p.id} to={`/productions/${p.id}`} className="nav-item">
                <Clock size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} aria-hidden="true" />
                <span className="nav-label" style={{ display: 'inline' }}>{p.name}</span>
              </NavLink>
            ))}
          </RailFlyoutButton>
        </div>
      ) : (
        <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border-subtle)', marginTop: '0.75rem' }}>
          <button
            type="button"
            onClick={toggleRecentOpen}
            aria-expanded={recentOpen}
            aria-controls="sidebar-section-recent"
            title={recentOpen ? t('collapseSection') : t('expandSection')}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, width: '100%',
              border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
              fontSize: 'var(--text-tiny)', fontWeight: 700, color: 'var(--text-muted)',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '0 12px 4px',
            }}
          >
            {recentOpen ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
            <span>{t('recentProductions')}</span>
          </button>
          {recentOpen && (
            <div id="sidebar-section-recent">
              {recentProductions.map(p => (
                <NavLink
                  key={p.id}
                  to={`/productions/${p.id}`}
                  className={({ isActive }) => isActive ? 'nav-item nav-active' : 'nav-item'}
                  style={{ display: 'block', padding: '4px 12px', fontSize: 'var(--text-caption)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {p.name}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ))}

      <DemoTimer />

      <div className="sidebar-footer">
        <div className="user-chip" onClick={() => onShowProfile()} role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowProfile() } }}
          style={{ cursor: 'pointer', width: '100%' }}>
          {user?.avatar_url && !avatarBroken ? (
            <img src={user.avatar_url} alt="Avatar" className="user-avatar" loading="lazy"
              style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-default)', flexShrink: 0 }}
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <div className="user-avatar">{user?.username?.[0]?.toUpperCase() || 'U'}</div>
          )}
          <div className="user-info"><span className="user-name">{user?.username}</span></div>
        </div>
        <div className="sidebar-footer-actions">
          <div style={{ position: 'relative' }}>
            <button className="btn btn-ghost btn-sm sidebar-notif-btn" onClick={() => navigate('/notifications')} title={t('notifications')} aria-label={t('notifications')} style={{ position: 'relative' }}>
              <Bell size={16} />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: -4, right: -4, background: 'var(--danger)', color: 'var(--text-primary)', borderRadius: '50%', fontSize: '0.7rem', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </div>
          {!sidebarCollapsed && (
            <select className="btn btn-ghost btn-sm" value={lang} onChange={e => setLang(e.target.value)} title={t('changeLanguage')} aria-label={t('changeLanguage')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 600, padding: '4px 2px' }}>
              {languageOptions.map((o: LanguageOption) => <option key={o.code} value={o.code}>{o.short}</option>)}
            </select>
          )}
          <button className="btn btn-ghost btn-sm" onClick={toggleTheme} title={getThemeTitle()} aria-label={t('ariaChangeTheme')}>
            {theme === 'dark' ? <Moon size={16} /> : theme === 'medium' ? <SunDim size={16} /> : <Sun size={16} />}
          </button>
          {!sidebarCollapsed && (
            <button className="btn btn-ghost btn-sm" onClick={() => { useAuthStore.getState().setSimpleMode(true); navigate('/simple') }} title={t('switchToSimple')} aria-label={t('simpleMode')}>
              <Smartphone size={16} />
            </button>
          )}
          {isGlobalAdmin?.() && !sidebarCollapsed && (
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/super-admin')} title={t('navPlatformMode')} aria-label={t('navPlatformMode')}>
              <ExternalLink size={16} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={logout} title={t('logout')} aria-label={t('logout')}><Power size={16} /></button>
        </div>
      </div>
      {sidebarCollapsed && tip && (
        <div className="sidebar-tooltip" style={{ left: 76, top: tip.top }}>{tip.label}</div>
      )}
    </aside>
  )
}
